import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import CustomButton from 'components/CustomButton';
import useAuth from 'contexts/useAuth';
import { queryClient } from 'index';
import { useState } from 'react';
import { FaSearch, FaTrash } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import {
  searchCustomerByAccount,
  getMyAccountAccess,
  addMyAccountAccess,
  removeMyAccountAccess,
  CustomerSearchResult,
} from 'services/api.service';

interface AddAccessFormData {
  accountNumber: string;
  validTo: string;
  isMaster: boolean;
}

export default function ManageMyAccountAccess() {
  const { userData } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchedCustomer, setSearchedCustomer] = useState<CustomerSearchResult | null>(null);

  const { data: accessesData, refetch } = useQuery({
    queryKey: ['myAccountAccess', userData.id],
    queryFn: () => getMyAccountAccess(userData.id),
  });

  // Ensure accesses is always an array
  const accesses = Array.isArray(accessesData) ? accessesData : [];

  const { register, handleSubmit, watch, reset } = useForm<AddAccessFormData>({
    defaultValues: {
      isMaster: false,
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 ano
    },
  });

  const isMaster = watch('isMaster');
  const accountNumber = watch('accountNumber');

  // Helper para extrair mensagem de erro da API
  const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.message) {
      return error.message;
    }
    return 'Erro desconhecido';
  };

  const handleSearch = async () => {
    if (!accountNumber || accountNumber.length < 5) {
      toast({
        title: 'Atenção',
        description: 'Digite um número de conta válido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    try {
      setSearchLoading(true);
      const customer = await searchCustomerByAccount(accountNumber.replace('-', ''));

      if (!customer) {
        toast({
          title: 'Conta não encontrada',
          description: 'Não foi possível encontrar uma conta com este número',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setSearchedCustomer(null);
        return;
      }

      setSearchedCustomer(customer);

      // Verifica se já tem acesso
      const alreadyHasAccess = accesses.some((access: any) => access.customerAccessId === customer.id);
      if (alreadyHasAccess) {
        toast({
          title: 'Acesso já existe',
          description: 'Esta conta já possui acesso à sua conta',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setSearchedCustomer(null);
        return;
      }
    } catch (error: any) {
      setSearchedCustomer(null);
      const errorMessage = getErrorMessage(error);
      toast({
        title: 'Erro ao buscar conta',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddAccess = handleSubmit(async (data) => {
    if (!searchedCustomer) {
      toast({
        title: 'Atenção',
        description: 'Busque uma conta primeiro',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    try {
      setLoading(true);

      await addMyAccountAccess(userData.id, {
        customerAccessId: searchedCustomer.id,
        validTo: new Date(data.validTo).toISOString(),
        isMaster: data.isMaster,
      });

      toast({
        title: 'Sucesso',
        description: 'Acesso adicionado com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      refetch();
      queryClient.refetchQueries({ queryKey: ['myAccountAccess'] });
      reset();
      setSearchedCustomer(null);
      onClose();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast({
        title: 'Erro ao adicionar acesso',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  });

  const handleRemoveAccess = async (accessId: string) => {
    if (!window.confirm('Tem certeza que deseja remover este acesso?')) return;

    try {
      await removeMyAccountAccess(userData.id, accessId);

      toast({
        title: 'Sucesso',
        description: 'Acesso removido com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      refetch();
      queryClient.refetchQueries({ queryKey: ['myAccountAccess'] });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast({
        title: 'Erro ao remover acesso',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const currentApprovals = userData?.withdrawalApprovalsRequired || 0;
  const masterCount = accesses.filter((access: any) => access.isMaster).length;

  return (
    <VStack
      align="stretch"
      spacing={4}
    >
      <Box>
        <HStack
          justify="space-between"
          mb={4}
        >
          <Box>
            <Text
              fontWeight="semibold"
              mb={1}
            >
              Gerenciar Acessos à Minha Conta
            </Text>
            <Text
              fontSize="sm"
              color="gray.600"
            >
              Permita que outras contas acessem e gerenciem sua conta. Gestores master podem aprovar saques.
            </Text>
          </Box>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={onOpen}
          >
            Adicionar Acesso
          </Button>
        </HStack>

        {currentApprovals > 0 && (
          <Box
            p={3}
            bg="blue.50"
            borderRadius="md"
            mb={4}
          >
            <Text
              fontSize="sm"
              color="blue.800"
            >
              <strong>Aprovações configuradas:</strong> {currentApprovals} aprovação{currentApprovals > 1 ? 'ões' : ''}{' '}
              necessária{currentApprovals > 1 ? 's' : ''} | <strong>Gestores master:</strong> {masterCount}
            </Text>
          </Box>
        )}
      </Box>

      {accesses?.length === 0 ? (
        <Box
          p={6}
          textAlign="center"
          bg="gray.50"
          borderRadius="md"
        >
          <Text color="gray.500">Nenhum acesso configurado</Text>
        </Box>
      ) : (
        <Box
          overflowX="auto"
          borderWidth="1px"
          borderRadius="md"
        >
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Conta</Th>
                <Th>Usuário</Th>
                <Th>E-mail</Th>
                <Th>Tipo</Th>
                <Th>Válido até</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {accesses.map((access: any) => {
                const isExpired = new Date(access.validTo) < new Date();
                return (
                  <Tr key={access.id}>
                    <Td fontSize="sm">{access.customerAccess.accountNumber}</Td>
                    <Td>{access.customerAccess.name}</Td>
                    <Td fontSize="sm">{access.customerAccess.email}</Td>
                    <Td>
                      {access.isMaster ? (
                        <Badge colorScheme="purple">Master</Badge>
                      ) : (
                        <Badge colorScheme="gray">Normal</Badge>
                      )}
                    </Td>
                    <Td fontSize="sm">
                      <Text color={isExpired ? 'red.500' : 'inherit'}>
                        {new Date(access.validTo).toLocaleDateString('pt-BR')}
                        {isExpired && ' (Expirado)'}
                      </Text>
                    </Td>
                    <Td>
                      <IconButton
                        aria-label="Remover acesso"
                        icon={<FaTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleRemoveAccess(access.id)}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal para adicionar acesso */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar Acesso à Minha Conta</ModalHeader>
          <ModalCloseButton />

          <form onSubmit={handleAddAccess}>
            <ModalBody>
              <VStack
                spacing={4}
                align="stretch"
              >
                <FormControl isRequired>
                  <FormLabel>Número da Conta</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FaSearch color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Digite o número da conta (ex: 123456-7)"
                      {...register('accountNumber')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />
                  </InputGroup>
                  <FormHelperText>Digite o número da conta e clique em "Buscar"</FormHelperText>
                  <Button
                    mt={2}
                    size="sm"
                    onClick={handleSearch}
                    isLoading={searchLoading}
                    leftIcon={<FaSearch />}
                  >
                    Buscar Conta
                  </Button>
                </FormControl>

                {searchedCustomer && (
                  <Box
                    p={4}
                    bg="green.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="green.200"
                  >
                    <Text
                      fontWeight="semibold"
                      color="green.800"
                      mb={2}
                    >
                      ✓ Conta encontrada
                    </Text>
                    <VStack
                      align="start"
                      spacing={1}
                    >
                      <Text fontSize="sm">
                        <strong>Nome:</strong> {searchedCustomer.name}
                      </Text>
                      <Text fontSize="sm">
                        <strong>E-mail:</strong> {searchedCustomer.email}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Conta:</strong> {searchedCustomer.accountNumber}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Documento:</strong> {searchedCustomer.document_number}
                      </Text>
                    </VStack>
                  </Box>
                )}

                <FormControl isRequired>
                  <FormLabel>Válido até</FormLabel>
                  <Input
                    type="date"
                    {...register('validTo')}
                  />
                  <FormHelperText>Data de expiração do acesso</FormHelperText>
                </FormControl>

                <FormControl>
                  <Checkbox
                    {...register('isMaster')}
                    colorScheme="purple"
                  >
                    <Text fontWeight="semibold">Acesso Master</Text>
                  </Checkbox>
                  <FormHelperText>
                    {isMaster
                      ? '✓ Este usuário poderá aprovar saques da sua conta'
                      : 'Este usuário terá acesso normal (sem permissão para aprovar saques)'}
                  </FormHelperText>
                </FormControl>

                {isMaster && (
                  <Box
                    p={3}
                    bg="purple.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="purple.200"
                  >
                    <Text
                      fontSize="sm"
                      color="purple.800"
                    >
                      <strong>Atenção:</strong> Gestores master podem aprovar ou rejeitar saques da sua conta. Configure
                      a quantidade de aprovações necessárias nas configurações da conta.
                    </Text>
                  </Box>
                )}

                {isMaster && currentApprovals === 0 && (
                  <Box
                    p={3}
                    bg="orange.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="orange.200"
                  >
                    <Text
                      fontSize="sm"
                      color="orange.800"
                    >
                      <strong>Aviso:</strong> Sua conta não exige aprovações de saques. Configure a quantidade de
                      aprovações necessárias nas configurações para que gestores master possam aprovar.
                    </Text>
                  </Box>
                )}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={loading}
              >
                Cancelar
              </Button>
              <CustomButton
                colorScheme="blue"
                onClick={handleAddAccess}
                isLoading={loading}
                isDisabled={!searchedCustomer}
              >
                Adicionar
              </CustomButton>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
